import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col, Card, Nav, Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { generateGUID, fetchTemplatesByCustomer } from "assets/js/common";
import { useDropzone } from "react-dropzone";
import Flex from "components/common/flex";
import cloudUpload from "assets/img/icons/cloud-upload.svg";
import { useAppContext } from "providers/appProvider";
import IconButton from "components/common/iconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { describeRecurrence, getNthWeekdayOfMonth, getWeekdayKey, getOccurrenceLabel, localTimeToUtcHHmm } from "../../utils/recurrenceHelpers";
import { setHours, setMinutes } from "date-fns";
import TimeInput from "../proposals/timeInput";

const ImportProposalWizard = ({
    show,
    handleClose,
    onSaveFinal,
    proposalId,
    relationId,
    relationInfo
}) => {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        clearErrors,
        reset,
        setValue,
        getValues,
        watch
    } = useForm({
        defaultValues: {
            startDate: null,
            uploadedPdf: null
        },
    });

    const { t, i18n } = useTranslation();
    const [step, setStep] = useState(1);
    const [selectedWorkType, setSelectedWorkType] = useState(null);
    const [proposalData, setProposalData] = useState({});
    const [taskOptions, setTaskOptions] = useState([]);
    const [selectedFrequency, setSelectedFrequency] = useState("Weekly");
    const [selectedDays, setSelectedDays] = useState([]);
    const [monthlyDay, setMonthlyDay] = useState(new Date().getDate());
    const [monthlyType, setMonthlyType] = useState("specific_day");
    const [monthlyOccurrences, setMonthlyOccurrences] = useState([{ occurrence: "1st", weekday: "monday" }]);
    const [oneTimeDate, setOneTimeDate] = useState(null);
    const [dailyStartTimes, setDailyStartTimes] = useState([setHours(setMinutes(new Date(), 0), 8)]);
    const [yearlyDates, setYearlyDates] = useState([{ day: new Date().getDate(), month: new Date().getMonth() }]);
    const MAX_MONTHLY_OCCURRENCE_ROWS = 4;
    const MAX_YEARLY_OCCURRENCE_ROWS = 6;
    const [isLoading, setIsLoading] = useState(false);
    const startDate = watch("startDate");
    const taskId = watch("taskId");
    const [tasks, setTasks] = useState([]);
    const [taskPendingDeleteIdx, setTaskPendingDeleteIdx] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [uploadedPdf, setUploadedPdf] = useState(null);
    const [uploadedPdfName, setUploadedPdfName] = useState("");
    const customerId = localStorage.getItem("customerId");

    const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    const navItems = [
        { icon: "file-pdf", label: t("proposalDetails") },
        { icon: "tasks", label: t("taskDetails") },
    ];

    useEffect(() => {
        if (show) {
            fetchTaskData();
            setStep(1);
        }
    }, [show]);

    const {
        config: { location: userLocation },
    } = useAppContext();

    const fetchTaskData = async () => {
        if (!customerId) return;
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/taskRoute/tasks`, {
            params: { customerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
        });
        setTaskOptions((res.data || []).map(task => ({
            value: task.TaskId,
            label: task.TaskName,
        })));
    };

    const handleDayToggle = (dayKey) => {
        setSelectedDays((prev) =>
            prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
        );
    };

    const addMonthlyOccurrenceRow = () => {
        setMonthlyOccurrences((prev) =>
            prev.length >= MAX_MONTHLY_OCCURRENCE_ROWS
                ? prev
                : [...prev, { occurrence: "1st", weekday: "monday" }]
        );
    };

    const removeMonthlyOccurrenceRow = (idx) => {
        setMonthlyOccurrences((prev) =>
            prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
        );
    };

    const addYearlyDateRow = () => {
        setYearlyDates((prev) =>
            prev.length >= MAX_YEARLY_OCCURRENCE_ROWS
                ? prev
                : [...prev, { day: 1, month: 0 }]
        );
    };

    const removeYearlyDateRow = (idx) => {
        setYearlyDates((prev) =>
            prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
        );
    };

    const addDailyStartTimeRow = () => {
        const lastTime = dailyStartTimes[dailyStartTimes.length - 1];
        const lastMoment = moment(lastTime);
        const fourHoursLater = lastMoment.clone().add(4, "hours");
        const latestSlotToday = lastMoment.clone().hour(23).minute(45).second(0).millisecond(0);

        // Clamp to the last valid slot (23:45) if +4h would roll into the next day.
        const candidate = fourHoursLater.isSame(lastMoment, "day") ? fourHoursLater : latestSlotToday;

        if (!candidate.isAfter(lastMoment)) {
            toast.error(t("dailyTimeOutOfRange") || "No more time slots left in the day.", {
                position: "top-right",
                autoClose: 2500,
            });
            return;
        }

        const candidateStr = candidate.format("HH:mm");
        const isDuplicate = dailyStartTimes.some((t) => moment(t).format("HH:mm") === candidateStr);
        if (isDuplicate) {
            toast.error(t("dailyTimeDuplicate") || "That time is already added.", {
                position: "top-right",
                autoClose: 2500,
            });
            return;
        }

        setDailyStartTimes((prev) => [...prev, candidate.toDate()]);
    };

    const removeDailyStartTimeRow = (idx) => {
        setDailyStartTimes((prev) =>
            prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
        );
    };

    const getWeekdayLabel = (day) => {
        if (!day) return "";
        return t(`fullDaysName.${day}`);
    };

    const getMonthLabel = (mIdx) => {
        const monthName = moment.months()[mIdx];
        return monthName ? t(`months.${monthName.toLowerCase()}`) : "";
    };

    const getOrdinalSuffix = (day, lang) => {
        if (lang === "nl") return "e";
        if (lang === "hi") return "";
        if (day === 1 || day === 21 || day === 31) return "st";
        if (day === 2 || day === 22) return "nd";
        if (day === 3 || day === 23) return "rd";
        return "th";
    };

    useEffect(() => {
        const refDate = startDate ? new Date(startDate) : new Date();
        const refDay = getWeekdayKey(refDate.getDay());
        switch (selectedFrequency) {
            case "OneTime":
                setSelectedDays([]);
                dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
                setOneTimeDate(refDate);
                break;
            case "Daily":
                setSelectedDays([...dayKeys]);
                dayKeys.forEach((day) => setValue(`weekdays.${day}`, true));
                break;
            case "Weekly": {
                const initDays = dayKeys.includes(refDay) ? [refDay] : [];
                setSelectedDays(initDays);
                dayKeys.forEach((day) => setValue(`weekdays.${day}`, initDays.includes(day)));
                break;
            }
            case "Monthly": {
                setMonthlyDay(refDate.getDate());
                const { nth, weekday } = getNthWeekdayOfMonth(refDate);
                setMonthlyOccurrences([
                    { occurrence: getOccurrenceLabel(nth), weekday: getWeekdayKey(weekday) },
                ]);
                break;
            }
            case "Yearly": {
                setYearlyDates([{ day: refDate.getDate(), month: refDate.getMonth() }]);
                break;
            }
            default:
                setSelectedDays([]);
                dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
        }
    }, [selectedFrequency, startDate, setValue]);

    const getRecurrenceSummary = () => {
        if (!startDate || !selectedFrequency) return null;

        const dayNames = selectedDays.map((day) => getWeekdayLabel(day)).join(" , ");

        switch (selectedFrequency) {
            case "OneTime": {
                const formattedDate = moment(oneTimeDate || startDate).format("Do MMMM YYYY");
                return t("recurrenceSummaryOnce", { date: formattedDate });
            }
            case "Daily": {
                const sortedTimes = [...dailyStartTimes]
                    .map((t) => moment(t).format("HH:mm"))
                    .sort();
                if (sortedTimes.length <= 1) {
                    return t("recurrenceSummaryDailySingle", { time: sortedTimes[0] });
                }
                return t("recurrenceSummaryDailyMulti", { count: sortedTimes.length, times: sortedTimes.join(", ") });
            }
            case "Weekly": {
                if (!dayNames) return "";
                return t("recurrenceSummaryWeekly", { days: dayNames });
            }
            case "Monthly": {
                if (monthlyType === "specific_day") {
                    const dayText = monthlyDay === "last" ? t("recurrenceSummaryMonthlyLastDay") : t("recurrenceSummaryMonthlyDayN", { day: monthlyDay });
                    return t("recurrenceSummaryMonthlyDayText", { dayText });
                } else {
                    const desc = monthlyOccurrences
                        .map((o) => `${t(o.occurrence)} ${getWeekdayLabel(o.weekday)}`)
                        .join(` ${t("listConjunction") || "and"} `);
                    return t("recurrenceSummaryMonthlyOccurrence", { desc });
                }
            }
            case "Yearly": {
                const desc = yearlyDates
                    .map((entry) => {
                        const monthsStr = getMonthLabel(entry.month);
                        if (entry.day === "last") {
                            return t("recurrenceSummaryYearlyLastDay", { month: monthsStr });
                        }
                        const suffix = getOrdinalSuffix(entry.day, i18n.language);
                        return t("recurrenceSummaryYearlyDateAlt", { day: entry.day, suffix, month: monthsStr });
                    })
                    .join(` ${t("listConjunction") || "and"} `);
                return t("recurrenceSummaryYearly", { desc });
            }
            default:
                return "";
        }
    };

    const onDrop = (acceptedFiles, fileRejections) => {
        if (fileRejections.length > 0) {
            setValue("uploadedPdf", null, { shouldValidate: true });
            return;
        }
        const file = acceptedFiles[0];
        if (!file) return;
        setUploadedPdf(file);
        setUploadedPdfName(file.name);
        setValue("uploadedPdf", file, { shouldValidate: true });
        clearErrors("uploadedPdf");
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        multiple: false,
        accept: { "application/pdf": [".pdf"] }
    });

    const handleAddTask = () => {
        const values = getValues();
        if (!isTaskValid) {
            toast.error(t("fillAllTaskFields"), { position: "top-right", autoClose: 2000 });
            return;
        }
        const taskName = taskOptions.find((opt) => opt.value === values.taskId)?.label || "";

        const advancedRecurrence = {};
        if (selectedFrequency === "Daily") {
            advancedRecurrence.times = [...dailyStartTimes]
                .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
                .map((t) => localTimeToUtcHHmm(t));
        } else if (selectedFrequency === "Monthly") {
            advancedRecurrence.monthlyType = monthlyType;
            if (monthlyType === "specific_day") {
                advancedRecurrence.monthlyDay = monthlyDay;
            } else {
                advancedRecurrence.monthlyOccurrences = monthlyOccurrences;
            }
        } else if (selectedFrequency === "Yearly") {
            advancedRecurrence.dates = yearlyDates;
        } else if (selectedFrequency === "OneTime") {
            advancedRecurrence.date = oneTimeDate
                ? moment(oneTimeDate).format("YYYY-MM-DD")
                : null;
        }

        const newTask = {
            TaskId: values.taskId,
            TaskName: taskName,
            Frequency: selectedFrequency,
            SelectedDays: selectedFrequency === "Weekly" ? selectedDays : advancedRecurrence,
            MonthlyDay: selectedFrequency === "Monthly" && monthlyType === "specific_day" ? monthlyDay : null,
            Summary: getRecurrenceSummary(),
        };
        setTasks((prev) => [...prev, newTask]);
        setValue("taskId", null);
        setValue("frequency", null);
        setSelectedFrequency("Weekly");
        setSelectedDays([]);
    };

    const handleProposalNext = (data) => {
        setProposalData(data);
        setStep(2);
    };

    const handleNavs = (targetStep) => {
        if (targetStep < step) {
            setStep(targetStep);
        } else if (targetStep === 2 && step === 1) {
            handleSubmit(handleProposalNext)();
        }
    };

    useEffect(() => {
        const loadTemplates = async () => {
            const data = await fetchTemplatesByCustomer(i18n.language);
            setTemplates(data);
        };
        loadTemplates();
    }, [i18n.language]);

    const generateProposalNumber = async () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, "0");
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const year = String(now.getFullYear()).slice(-2);
        const hour = String(now.getHours()).padStart(2, "0");
        const second = String(now.getSeconds()).padStart(2, "0");
        const milli = String(now.getMilliseconds()).padStart(3, "0");
        return `OR${day}${month}${year}${hour}${second}${milli}`;
    };

    const saveProposal = async (proposalData) => {
        const userIdFromSession = localStorage.getItem("UserId");
        if (!userIdFromSession) throw new Error("UserId is missing in localStorage");
        const userResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userIdFromSession}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        const employeeNumber = userResponse?.data?.UserNumber || null;
        const proposalNumber = await generateProposalNumber();
        const finalProposalId = proposalId || generateGUID();
        const { editStartDate, editEndDate, editStartTime, editEndTime, ...cleanedData } = proposalData;
        const formattedStartDate = proposalData.startDate ? moment(proposalData.startDate).format("YYYY-MM-DD") : null;
        const formattedEndDate = proposalData.endDate ? moment(proposalData.endDate).format("YYYY-MM-DD") : null;
        const formattedDueDate = formattedStartDate ? moment(formattedStartDate).add(14, "days").format("YYYY-MM-DD") : null;
        let formattedStartTime = null;
        if (proposalData.startTime) {
            const utcEquivalentMoment = moment(proposalData.startTime).utc();
            formattedStartTime = `${moment().format("YYYY-MM-DD")}T${utcEquivalentMoment.format("HH:mm:ss.SSS")}Z`;
        }
        const selectedTemplate = templates.find((t) => t.TemplateId === proposalData.templateId);
        const templateName = selectedTemplate?.TemplateName || null;
        const updatedFormData = {
            ...cleanedData,
            paymentTerms: "Bank Transfer",
            invoiceType: "Invoice",
            employee: employeeNumber,
            client: relationInfo.RelationNumber,
            proposalNumber,
            proposalId: finalProposalId,
            customerId,
            templateName,
            contactperson: relationInfo?.Contactperson || "Mock Contact",
            location: relationInfo?.City,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            date: formattedStartDate,
            dueDate: formattedDueDate,
            startTime: formattedStartTime ? moment(formattedStartTime).format("HH:mm:ss") : null,
            locationId: userLocation?.LocationId || null,
            status: "1",
            clientComment: null,
            proposalComment: null,
            isImported: true
        };
        delete updatedFormData.weekdays;
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/proposalRoute/addProposal`,
            updatedFormData,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        return response.data;
    };

    const saveOrUpdateProposalDetail = async ({ proposalId, relationId, locationId, document, templateId, templateName, workType, proposalVersionId }) => {
        return axios.post(
            `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/addUpdateProposalDetails`,
            { proposalId, relationId, locationId, workType, document, templateId, templateName, communicationUserId: localStorage.getItem("UserId"), proposalVersionId: proposalVersionId || null, bypassGate: true, },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
    };

    const saveTask = async (task, proposalId, proposalVersionId) => {
        const fullTask = {
            TaskId: task.TaskId,
            Frequency: task.Frequency,
            RepeatEvery: task.Frequency === "OneTime" ? 0 : task.RepeatEvery,
            SelectedDays: typeof task.SelectedDays === "object" && task.SelectedDays !== null
                ? JSON.stringify(task.SelectedDays)
                : task.SelectedDays,
            MonthlyDay: task.MonthlyDay,
            ProposalId: proposalId,
            ProposalVersionId: proposalVersionId || null,
            bypassGate: true,
        };
        const taskRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks/add`,
            fullTask,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        return taskRes.data;
    };

    const saveSubtasks = async (taskId, proposalTaskId, fullTask, proposalId) => {
        const subtasksRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${taskId}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        const fetchedSubtasks = subtasksRes.data || [];
        if (fetchedSubtasks.length > 0) {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/proposalRoute/subtasks/replace`,
                {
                    proposalId,
                    bypassGate: true,
                    subtasks: fetchedSubtasks.map((st) => ({
                        ProposalTaskId: proposalTaskId,
                        SubtaskId: st.SubtaskId,
                        SelectedDays: fullTask.SelectedDays,
                        MonthlyDay: fullTask.MonthlyDay,
                    })),
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
        }
    };

    const isTaskValid =
        Boolean(taskId) &&
        Boolean(selectedFrequency) &&
        (
            (selectedFrequency === "Daily" &&
                dailyStartTimes.length > 0 &&
                dailyStartTimes.every((t) => Boolean(t)) &&
                new Set(dailyStartTimes.map((t) => moment(t).format("HH:mm"))).size === dailyStartTimes.length) ||
            (selectedFrequency === "Weekly" && selectedDays.length > 0) ||
            (selectedFrequency === "Monthly" &&
                (monthlyType === "specific_day"
                    ? Boolean(monthlyDay)
                    : monthlyOccurrences.length > 0)) ||
            (selectedFrequency === "OneTime" && Boolean(oneTimeDate)) ||
            (selectedFrequency === "Yearly" && yearlyDates.length > 0)
        );

    const resetProposalForm = () => {
        reset();
        setStep(1);
        setTasks([]);
        setUploadedPdf(null);
        setUploadedPdfName("");
        setSelectedFrequency("Weekly");
        setSelectedDays([]);
        setMonthlyDay(new Date().getDate());
        setMonthlyType("specific_day");
        setMonthlyOccurrences([{ occurrence: "1st", weekday: "monday" }]);
        setOneTimeDate(null);
        setDailyStartTimes([setHours(setMinutes(new Date(), 0), 8)]);
        setYearlyDates([{ day: new Date().getDate(), month: new Date().getMonth() }]);
    };

    const uploadPdfToBlob = async (file, relationId, proposalId, proposalVersionId) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("proposalVersionId", proposalVersionId);
        formData.append("proposalId", proposalId);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/blobRoute/uploadBlob/${relationId}`,
            formData,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        return response.data.blobUrl;
    };

    const handleFinalSave = async () => {
        if (tasks.length === 0) {
            toast.error(t("saveAtLeastOneTask"), { position: "top-right", autoClose: 2000 });
            return;
        }
        setIsLoading(true);
        try {
            const createdProposal = await saveProposal(proposalData);
            const savedProposalId = createdProposal.ProposalId;
            const createdVersionId = createdProposal.CurrentVersionId || null;
            let proposalDocumentUrl = null;
            if (uploadedPdf) {
                proposalDocumentUrl = await uploadPdfToBlob(uploadedPdf, relationInfo.RelationId, savedProposalId, createdVersionId);
            }
            const selectedTemplate = templates.find(t => t.TemplateId === proposalData.templateId);
            await saveOrUpdateProposalDetail({
                proposalId: savedProposalId,
                relationId: relationInfo.RelationId,
                locationId: userLocation?.LocationId || null,
                document: proposalDocumentUrl,
                templateId: selectedTemplate?.TemplateId || null,
                templateName: selectedTemplate?.TemplateName || null,
                workType: selectedTemplate?.WorkType,
                proposalVersionId: createdVersionId || null,
            });
            for (const task of tasks) {
                const savedTask = await saveTask(task, savedProposalId, createdVersionId);
                await saveSubtasks(task.TaskId, savedTask.ProposalTaskId, task, savedProposalId);
            }
            toast.success(t("proposalTasksSaved"), { position: "top-right", autoClose: 2000 });
            resetProposalForm();
            onSaveFinal?.();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error(t("proposalSaveFailed"), { position: "top-right", autoClose: 2000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <Card
            as={Form}
            noValidate
            onSubmit={(e) => e.preventDefault()}
            className="theme-wizard mb-0 shadow-none"
        >
            {/* ── Wizard Nav Header ── */}
            <Card.Header className="bg-body-tertiary pb-2">
                <Nav className="justify-content-center nav-wizard">
                    {navItems.map((item, index) => (
                        <Nav.Item key={item.label}>
                            <Nav.Link
                                className={classNames("fw-semibold", {
                                    done: step > index + 1,
                                    active: step === index + 1
                                })}
                                onClick={() => handleNavs(index + 1)}
                                style={{ cursor: index + 1 < step ? "pointer" : "default" }}
                            >
                                <span className="nav-item-circle-parent">
                                    <span className="nav-item-circle">
                                        <FontAwesomeIcon icon={item.icon} />
                                    </span>
                                </span>
                                <span className="d-none d-md-block mt-1 fs-10">{item.label}</span>
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </Card.Header>

            {/* ── Progress Bar ── */}
            <div className="progress" style={{ height: 2, borderRadius: 0 }}>
                <div
                    className="progress-bar"
                    style={{ width: `${step * 50}%`, transition: "width 0.3s ease" }}
                />
            </div>

            {/* ── Step Body ── */}
            <Card.Body className="fw-normal px-4 py-4">

                {/* Step 1 – Proposal Details */}
                {step === 1 && (
                    <Row>
                        <Col md="6" className="border-end">
                            <Form.Label className="fw-semibold mb-2">
                                {t("uploadPdf")} <span className="text-danger">*</span>
                            </Form.Label>
                            <input
                                type="hidden"
                                {...register("uploadedPdf", { required: t("uploadPdf") })}
                            />
                            <div
                                {...getRootProps({
                                    className: `dropzone-area py-4 rounded text-center ${errors.uploadedPdf ? "is-invalid" : "border-300"}`
                                })}
                                style={{ cursor: "pointer", height: "120px", display: "flex", flexDirection: "column", justifyContent: "center" }}
                            >
                                <input {...getInputProps()} />
                                <Flex justifyContent="center" alignItems="center">
                                    <img src={cloudUpload} alt="" width={26} className="me-2" />
                                    <p className="fs-9 mb-0 text-700">{t("dropPdfHere")}</p>
                                </Flex>
                            </div>
                            <div className="invalid-feedback d-block">{errors.uploadedPdf?.message}</div>
                            {uploadedPdfName && (
                                <div className="mt-3 d-flex align-items-center text-success fs-9">
                                    <i className="fa fa-file-pdf me-2" />
                                    {uploadedPdfName}
                                </div>
                            )}
                        </Col>

                        <Col md="6">
                            <Form.Label className="data-font-size">
                                {t("workType")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Group className="mb-3">
                                <Controller
                                    name="templateId"
                                    control={control}
                                    rules={{ required: t("workTypeRequired") }}
                                    render={({ field, fieldState: { error } }) => {
                                        const workTypeOptions = templates.map((template) => ({
                                            value: template.TemplateId,
                                            label: template.TemplateName,
                                            workType: template.WorkType,
                                        }));
                                        return (
                                            <>
                                                <input type="hidden" {...register("workType")} />
                                                <Select
                                                    key={i18n.language}
                                                    options={workTypeOptions}
                                                    isClearable
                                                    value={workTypeOptions.find((opt) => opt.value === field.value) || null}
                                                    onChange={(selected) => {
                                                        field.onChange(selected ? selected.value : "");
                                                        setValue("workType", selected ? selected.workType : "");
                                                        setSelectedWorkType(selected ? selected.workType : "");
                                                    }}
                                                    className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                                    classNamePrefix="custom-select"
                                                    placeholder={t("select")}
                                                />
                                                {error && <div className="invalid-feedback d-block">{error.message}</div>}
                                            </>
                                        );
                                    }}
                                />
                            </Form.Group>

                            <Form.Label>
                                {t("startDate")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                                control={control}
                                rules={{ required: t("startDateRequired") }}
                                name="startDate"
                                render={({ field }) => (
                                    <DatePicker
                                        selected={field.value}
                                        onChange={field.onChange}
                                        dateFormat="dd-MM-yyyy"
                                        className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                                        placeholderText="dd-mm-yyyy"
                                    />
                                )}
                            />
                            {errors.startDate && (
                                <div className="invalid-feedback d-block">{errors.startDate.message}</div>
                            )}
                        </Col>
                    </Row>
                )}

                {/* Step 2 – Task Details */}
                {step === 2 && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label className="data-font-size fw-semi-bold">
                                {t("task")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                                name="taskId"
                                control={control}
                                rules={{ required: t("taskRequired") }}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <Select
                                            key={i18n.language}
                                            options={taskOptions}
                                            isClearable
                                            value={taskOptions.find((opt) => opt.value === field.value) || null}
                                            onChange={(s) => field.onChange(s ? s.value : null)}
                                            onBlur={field.onBlur}
                                            className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                            classNamePrefix="custom-select"
                                            placeholder={t("select")}
                                            noOptionsMessage={() => t("noOptionsAvailable")}
                                        />
                                        {error && <div className="invalid-feedback d-block">{error.message}</div>}
                                    </>
                                )}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="data-font-size fw-semi-bold">
                                {t("frequency")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                                name="frequency"
                                control={control}
                                rules={{ required: t("frequencyRequired") }}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <Nav
                                            variant="pills"
                                            activeKey={selectedFrequency}
                                            onSelect={(selectedKey) => {
                                                field.onChange(selectedKey);
                                                setSelectedFrequency(selectedKey);
                                            }}
                                            className="frequency-tabs-nav mb-1"
                                        >
                                            <Nav.Item>
                                                <Nav.Link eventKey="OneTime">{t("onetime")}</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="Daily">{t("daily")}</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="Weekly">{t("weekly")}</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="Monthly">{t("monthly")}</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="Yearly">{t("yearly")}</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        {error && !selectedFrequency && (
                                            <div className="invalid-feedback d-block">{error.message}</div>
                                        )}
                                    </>
                                )}
                            />
                        </Form.Group>

                        {selectedFrequency === "OneTime" && (
                            <div className="recurrence-settings-card">
                                <div className="recurrence-settings-header">
                                    <i className="far fa-clock" />
                                    <span>{t("recurrenceSettings")}</span>
                                </div>
                                <Form.Group className="mb-0">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("taskDate") || "Task Date"} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="task-date-input-wrap">
                                        <DatePicker
                                            selected={oneTimeDate}
                                            onChange={(selectedDate) => setOneTimeDate(selectedDate)}
                                            dateFormat="dd-MM-yyyy"
                                            className="form-control"
                                            placeholderText="dd-mm-yyyy"
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                        )}

                        {selectedFrequency === "Daily" && (
                            <div className="recurrence-settings-card">
                                <div className="recurrence-settings-header">
                                    <i className="far fa-clock" />
                                    <span>{t("recurrenceSettings")}</span>
                                </div>
                                <Form.Group className="mb-0">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("startsAt")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    {dailyStartTimes.map((time, idx) => (
                                        <div key={idx} className="recurrence-occurrence-row">
                                            <DatePicker
                                                selected={time}
                                                onChange={(val) => {
                                                    const newTimeStr = moment(val).format("HH:mm");
                                                    const isDuplicate = dailyStartTimes.some(
                                                        (t, i) => i !== idx && moment(t).format("HH:mm") === newTimeStr
                                                    );
                                                    if (isDuplicate) {
                                                        toast.error(t("dailyTimeDuplicate") || "That time is already added.", {
                                                            position: "top-right",
                                                            autoClose: 2500,
                                                        });
                                                        return;
                                                    }
                                                    setDailyStartTimes((prev) => {
                                                        const arr = [...prev];
                                                        arr[idx] = val;
                                                        return arr;
                                                    });
                                                }}
                                                showTimeSelect
                                                showTimeSelectOnly
                                                timeIntervals={15}
                                                timeCaption="Time"
                                                dateFormat="HH:mm"
                                                timeFormat="HH:mm"
                                                placeholderText="HH:mm"
                                                className="form-control"
                                                customInput={
                                                    <TimeInput className="form-control" style={{ width: "100px" }} />
                                                }
                                            />
                                            {dailyStartTimes.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-day-btn"
                                                    onClick={() => removeDailyStartTimeRow(idx)}
                                                    aria-label={t("remove") || "Remove"}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mt-1 text-decoration-none add-day-link"
                                        onClick={addDailyStartTimeRow}
                                    >
                                        {t("addAnotherTime") || "+ Add another time"}
                                    </Button>
                                </Form.Group>
                            </div>
                        )}

                        {selectedFrequency === "Weekly" && (
                            <div className="recurrence-settings-card">
                                <div className="recurrence-settings-header">
                                    <i className="far fa-clock" />
                                    <span>{t("recurrenceSettings")}</span>
                                </div>
                                <Form.Group className="mb-0">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("weekday")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {dayKeys.map((dayKey) => (
                                            <div key={dayKey}>
                                                <input
                                                    type="checkbox"
                                                    {...register(`weekdays.${dayKey}`)}
                                                    checked={selectedDays.includes(dayKey)}
                                                    readOnly
                                                    hidden
                                                />
                                                <Button
                                                    variant={selectedDays.includes(dayKey) ? "success" : "outline-secondary"}
                                                    size="sm"
                                                    className="weekday-btn"
                                                    onClick={() => handleDayToggle(dayKey)}
                                                >
                                                    {t(`days.${dayKey}`)}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </Form.Group>
                            </div>
                        )}

                        {selectedFrequency === "Monthly" && (
                            <div className="recurrence-settings-card">
                                <div className="recurrence-settings-header">
                                    <i className="far fa-clock" />
                                    <span>{t("recurrenceSettings")}</span>
                                </div>
                                <Form.Group className="mb-3">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("repeatBy") || "Repeat By"} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Row className="g-2">
                                        <Col xs={6}>
                                            <div
                                                className={`recurrence-radio-card ${monthlyType === "weekday_occurrence" ? "active" : ""}`}
                                                onClick={() => setMonthlyType("weekday_occurrence")}
                                            >
                                                <Form.Check
                                                    type="radio"
                                                    name="monthlyTypeRadio"
                                                    checked={monthlyType === "weekday_occurrence"}
                                                    onChange={() => setMonthlyType("weekday_occurrence")}
                                                    className="mb-0"
                                                />
                                                <span>{t("dayOfWeek") || "Day of Week"}</span>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div
                                                className={`recurrence-radio-card ${monthlyType === "specific_day" ? "active" : ""}`}
                                                onClick={() => setMonthlyType("specific_day")}
                                            >
                                                <Form.Check
                                                    type="radio"
                                                    name="monthlyTypeRadio"
                                                    checked={monthlyType === "specific_day"}
                                                    onChange={() => setMonthlyType("specific_day")}
                                                    className="mb-0"
                                                />
                                                <span>{t("dayOfMonth") || "Day of Month"}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form.Group>

                                {monthlyType === "specific_day" ? (
                                    <Row className="g-3 mb-2">
                                        <Col xs={12} sm={5}>
                                            <Form.Group>
                                                <Form.Label className="data-font-size fw-semi-bold">
                                                    {t("onDay") || "On Day"} <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Select
                                                    value={monthlyDay}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setMonthlyDay(val === "last" ? "last" : Number(val));
                                                    }}
                                                    className="recurrence-inline-select"
                                                >
                                                    {[...Array(31).keys()].slice(1).map((num) => (
                                                        <option key={num} value={num}>{num}</option>
                                                    ))}
                                                    <option value="last">{t("lastDay") || "Last Day"}</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Form.Group className="mb-2">
                                        <Form.Label className="data-font-size fw-semi-bold">
                                            {t("weekday")} <span className="text-danger">*</span>
                                        </Form.Label>
                                        {monthlyOccurrences.map((occ, idx) => (
                                            <div key={idx} className="recurrence-occurrence-row">
                                                <Form.Select
                                                    value={occ.occurrence}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setMonthlyOccurrences((prev) => {
                                                            const arr = [...prev];
                                                            arr[idx] = { ...arr[idx], occurrence: val };
                                                            return arr;
                                                        });
                                                    }}
                                                    className="w-auto"
                                                >
                                                    <option value="1st">{t("1st") || "1st"}</option>
                                                    <option value="2nd">{t("2nd") || "2nd"}</option>
                                                    <option value="3rd">{t("3rd") || "3rd"}</option>
                                                    <option value="4th">{t("4th") || "4th"}</option>
                                                    <option value="last">{t("last") || "Last"}</option>
                                                </Form.Select>

                                                <div className="d-flex flex-nowrap gap-1">
                                                    {dayKeys.map((dayKey) => (
                                                        <Button
                                                            key={dayKey}
                                                            variant={occ.weekday === dayKey ? "success" : "outline-secondary"}
                                                            size="sm"
                                                            className="weekday-btn"
                                                            onClick={() => {
                                                                setMonthlyOccurrences((prev) => {
                                                                    const arr = [...prev];
                                                                    arr[idx] = { ...arr[idx], weekday: dayKey };
                                                                    return arr;
                                                                });
                                                            }}
                                                        >
                                                            {t(`days.${dayKey}`)}
                                                        </Button>
                                                    ))}
                                                </div>
                                                {monthlyOccurrences.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="remove-day-btn"
                                                        onClick={() => removeMonthlyOccurrenceRow(idx)}
                                                        aria-label={t("remove") || "Remove"}
                                                    >
                                                        &times;
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        {monthlyOccurrences.length < MAX_MONTHLY_OCCURRENCE_ROWS && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 mt-1 text-decoration-none add-day-link"
                                                onClick={addMonthlyOccurrenceRow}
                                            >
                                                {t("addAnotherDay") || "+ Add another day"}
                                            </Button>
                                        )}
                                    </Form.Group>
                                )}
                            </div>
                        )}

                        {selectedFrequency === "Yearly" && (
                            <div className="recurrence-settings-card">
                                <div className="recurrence-settings-header">
                                    <i className="far fa-clock" />
                                    <span>{t("recurrenceSettings")}</span>
                                </div>
                                <Form.Group className="mb-2">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("onDay") || "On Day"} <span className="text-danger">*</span>
                                    </Form.Label>
                                    {yearlyDates.map((entry, idx) => (
                                        <div key={idx} className="recurrence-occurrence-row">
                                            <Form.Select
                                                value={entry.day}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setYearlyDates((prev) => {
                                                        const arr = [...prev];
                                                        arr[idx] = { ...arr[idx], day: val === "last" ? "last" : Number(val) };
                                                        return arr;
                                                    });
                                                }}
                                                className="recurrence-inline-select"
                                            >
                                                {[...Array(31).keys()].slice(1).map((num) => (
                                                    <option key={num} value={num}>{num}</option>
                                                ))}
                                                <option value="last">{t("lastDay") || "Last Day"}</option>
                                            </Form.Select>
                                            <span className="text-muted font-size-13">{t("of") || "of"}</span>
                                            <Form.Select
                                                value={entry.month}
                                                onChange={(e) => {
                                                    const val = Number(e.target.value);
                                                    setYearlyDates((prev) => {
                                                        const arr = [...prev];
                                                        arr[idx] = { ...arr[idx], month: val };
                                                        return arr;
                                                    });
                                                }}
                                                className="recurrence-inline-select"
                                            >
                                                {moment.months().map((month, mIdx) => (
                                                    <option key={month} value={mIdx}>
                                                        {t(`shortMonths.${month.toLowerCase()}`)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {yearlyDates.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-day-btn"
                                                    onClick={() => removeYearlyDateRow(idx)}
                                                    aria-label={t("remove") || "Remove"}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    {yearlyDates.length < MAX_YEARLY_OCCURRENCE_ROWS && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 mt-1 text-decoration-none add-day-link"
                                            onClick={addYearlyDateRow}
                                        >
                                            {t("addAnotherDay") || "+ Add another day"}
                                        </Button>
                                    )}
                                </Form.Group>
                            </div>
                        )}

                        {getRecurrenceSummary() && (
                            <div className="recurrence-summary-box mb-3">
                                <div className="recurrence-summary-icon">
                                    <i className="fas fa-check-circle" />
                                </div>
                                <div className="recurrence-summary-content">
                                    <div className="recurrence-summary-title">
                                        {t("recurrenceSummary")}
                                    </div>
                                    <div className="recurrence-summary-text">
                                        {getRecurrenceSummary()}
                                    </div>
                                </div>
                            </div>
                        )}

                        {tasks.length > 0 && (
                            <div className="mt-4">
                                <hr />
                                <h5>{t("savedTasks")} ({tasks.length})</h5>
                                <div className="saved-task-list">
                                    {tasks.map((task, idx) => (
                                        <div key={idx} className="saved-task-row">
                                            <div className="saved-task-row-top">
                                                <span className="saved-task-name">{task.TaskName}</span>
                                                <span className="badge bg-primary-subtle text-primary saved-task-badge">
                                                    {t(task.Frequency.toLowerCase())}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="saved-task-delete-btn"
                                                    onClick={() => setTaskPendingDeleteIdx(idx)}
                                                    aria-label={`${t("delete")} ${task.TaskName}`}
                                                >
                                                    <FontAwesomeIcon icon="trash" />
                                                </button>
                                            </div>
                                            <div className="saved-task-schedule">
                                                {describeRecurrence(task, t)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>

            {/* ── Wizard Footer ── */}
            <Card.Footer className="d-flex bg-body-tertiary px-4">
                <IconButton
                    variant="link"
                    icon="chevron-left"
                    iconAlign="left"
                    transform="down-1 shrink-4"
                    className={classNames("px-0 fw-semibold", { "d-none": step === 1 })}
                    onClick={() => setStep(step - 1)}
                >
                    {t("back")}
                </IconButton>

                {step === 2 && (
                    <IconButton
                        variant="success"
                        icon="plus"
                        transform="down-1 shrink-4"
                        className="ms-auto"
                        onClick={handleAddTask}
                        disabled={!isTaskValid}
                    >
                        {tasks.length > 0 ? t("addAnotherTask") : t("addTask")}
                    </IconButton>
                )}

                {(step === 1 || (step === 2 && tasks.length > 0)) && (
                    <IconButton
                        variant="primary"
                        className={step === 2 ? "px-5 ms-2" : "ms-auto px-5"}
                        icon={step === 2 ? "save" : "chevron-right"}
                        iconAlign="right"
                        transform="down-1 shrink-4"
                        disabled={isLoading}
                        onClick={() => {
                            if (step === 1) handleSubmit(handleProposalNext)();
                            else handleFinalSave();
                        }}
                    >
                        {step === 1 ? t("next") : isLoading ? t("saving") : t("save")}
                    </IconButton>
                )}
            </Card.Footer>
        </Card>

        <Modal
            show={taskPendingDeleteIdx !== null}
            onHide={() => setTaskPendingDeleteIdx(null)}
            backdrop="static"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("confirmation")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {t("confirmDeleteTaskWithSubtasks", {
                    taskName: taskPendingDeleteIdx !== null ? tasks[taskPendingDeleteIdx]?.TaskName : "",
                })}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="falcon-default" onClick={() => setTaskPendingDeleteIdx(null)}>
                    {t("cancel")}
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        setTasks((prev) => prev.filter((_, i) => i !== taskPendingDeleteIdx));
                        setTaskPendingDeleteIdx(null);
                    }}
                >
                    {t("delete")}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default ImportProposalWizard;